import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { EmptyState, Field, Select, Spinner } from '@zt/ui';
import { Figure, Section, Table, td, tdR, th, thR, usePct } from '../components/reports/blocks';
import { PeriodControls, usePeriod } from '../components/reports/period';
import { PrintButton, PrintHeader } from '../components/reports/print';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useStoreChoice } from '../lib/store-choice';
import type { MarketingSummary } from '../lib/types-faz8';

/**
 * Pazarlama ve SEO özeti (F8-11, ADR-813): huni, müşteri edinme, 12 aylık müşteri değeri, reklam, organik arama,
 * e-posta/SMS ve son dokunuş kaynağı tek sayfada; ayrıntı için ilgili rapora bağlantı. Rehber:
 * docs/faz-8/REPORTS_FINANCE_TR.md#pazarlama-ozeti.
 */
export function MarketingSummaryPage() {
  const t = useT();
  const money = useMoney();
  const pct = usePct();
  const { formatNumber, formatDate } = useI18n();
  const period = usePeriod();
  const { stores } = useStoreChoice();
  const [storeId, setStoreId] = useState('');
  const params = new URLSearchParams({ from: period.from, to: period.to });
  if (storeId) params.set('storeId', storeId);
  const q = useQuery({
    queryKey: ['marketing-summary', params.toString()],
    queryFn: () => api.get<MarketingSummary>(`/v1/admin/reports/marketing?${params}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const n = (x: number) => formatNumber(x, 0);
  const tl = (x: number | null) => (x === null ? '—' : money(x, 'TRY'));
  return (
    <PageFrame helpKey="page.marketing_summary" wide printable actions={<PrintButton />}>
      <PrintHeader title={t('admin.marketingSummary.navLabel')} period={period} />
      <div className="flex flex-wrap items-end gap-4 print:hidden">
        <PeriodControls period={period} showInterval={false} />
        {stores.length > 1 && (
          <Field label={t('admin.salesReport.store')} help={<HelpTip k="sales_report.store" />}>
            {(p) => (
              <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                <option value="">{t('admin.reports.all')}</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </div>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div
          className={
            q.isFetching ? 'space-y-8 opacity-60 transition-opacity' : 'space-y-8 transition-opacity'
          }
        >
          {d.period.partial && <p className="text-xs text-ink-3">{t('admin.reports.partial')}</p>}
          <Section
            title={t('admin.marketingSummary.funnelTitle')}
            help={<HelpTip k="marketing_summary.funnel" />}
          >
            <ol aria-label={t('admin.marketingSummary.funnelTitle')} className="grid gap-3 sm:grid-cols-3">
              {(
                [
                  ['carts', d.funnel.carts, null],
                  ['checkouts', d.funnel.checkouts, d.funnel.cartToCheckoutBps],
                  ['orders', d.funnel.orders, d.funnel.checkoutToOrderBps],
                ] as const
              ).map(([k, v, rate]) => (
                <li key={k} className="rounded-md border border-line bg-surface p-4">
                  <p className="text-sm text-ink-3">{t(`admin.marketingSummary.funnel.${k}`)}</p>
                  <p className="text-2xl font-semibold text-ink">{n(v)}</p>
                  {rate !== null && (
                    <p className="text-xs text-ink-3">
                      {t('admin.marketingSummary.stepRate', { pct: pct(rate) })}
                    </p>
                  )}
                </li>
              ))}
            </ol>
            <p className="mt-2 text-xs text-ink-3">
              {t('admin.marketingSummary.funnelOverall', { pct: pct(d.funnel.cartToOrderBps) })}{' '}
              {t('admin.marketingSummary.funnelNote')}
            </p>
          </Section>

          <Section
            title={t('admin.marketingSummary.customersTitle')}
            help={<HelpTip k="marketing_summary.cac" />}
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Figure
                label={t('admin.marketingSummary.newCustomers')}
                value={n(d.customers.newCustomers)}
                sub={t('admin.marketingSummary.returning', { count: n(d.customers.returningCustomers) })}
              />
              <Figure
                label={t('admin.marketingSummary.cac')}
                value={tl(d.customers.cacMinor)}
                sub={
                  d.ads === null
                    ? t('admin.marketingSummary.cacHidden')
                    : d.ads.accounts === 0
                      ? t('admin.marketingSummary.noAds')
                      : t('admin.marketingSummary.cacOf', { spend: tl(d.ads.spend) })
                }
              />
              <Figure
                label={t('admin.marketingSummary.value12m')}
                value={tl(d.value12m.perCustomerMinor)}
                sub={t('admin.marketingSummary.value12mOf', {
                  customers: n(d.value12m.customers),
                  orders:
                    d.value12m.ordersPerCustomerX100 === null
                      ? '—'
                      : formatNumber(d.value12m.ordersPerCustomerX100 / 100, 2),
                })}
                help={<HelpTip k="marketing_summary.value" />}
              />
              <Figure
                label={t('admin.marketingSummary.valueToCac')}
                value={
                  d.customers.cacMinor && d.value12m.perCustomerMinor !== null
                    ? formatNumber(d.value12m.perCustomerMinor / d.customers.cacMinor, 1)
                    : '—'
                }
                sub={t('admin.marketingSummary.valueToCacNote')}
              />
            </div>
          </Section>

          {d.ads && (
            <Section title={t('admin.marketingSummary.adsTitle')}>
              {d.ads.accounts === 0 ? (
                <EmptyState title={t('admin.marketingSummary.noAds')} />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Figure label={t('admin.marketingSummary.adSpend')} value={tl(d.ads.spend)} />
                  <Figure label={t('admin.marketingSummary.clicks')} value={n(d.ads.clicks)} />
                  <Figure label={t('admin.marketingSummary.ctr')} value={pct(d.ads.ctrBps)} />
                  <Figure label={t('admin.marketingSummary.cpc')} value={tl(d.ads.cpcMinor)} />
                </div>
              )}
              <p className="mt-2 text-sm">
                <Link to="/reklam-merkezi" className="text-pine hover:underline">
                  {t('admin.marketingSummary.openAds')}
                </Link>
              </p>
            </Section>
          )}

          {d.seo && (
            <Section title={t('admin.marketingSummary.seoTitle')}>
              {!d.seo.linked ? (
                <EmptyState title={t('admin.marketingSummary.seoNotLinked')} />
              ) : (
                <>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Figure label={t('admin.marketingSummary.organicClicks')} value={n(d.seo.clicks)} />
                    <Figure label={t('admin.marketingSummary.impressions')} value={n(d.seo.impressions)} />
                    <Figure label={t('admin.marketingSummary.ctr')} value={pct(d.seo.ctrBps)} />
                    <Figure
                      label={t('admin.marketingSummary.position')}
                      value={d.seo.positionX10 === null ? '—' : formatNumber(d.seo.positionX10 / 10, 1)}
                    />
                  </div>
                  <p className="mt-2 text-xs text-ink-3">
                    {d.seo.lastDate
                      ? t('admin.marketingSummary.seoLast', { date: formatDate(d.seo.lastDate, false) })
                      : t('admin.marketingSummary.seoNoData')}
                  </p>
                </>
              )}
            </Section>
          )}

          {d.messaging && (
            <Section title={t('admin.marketingSummary.messagingTitle')}>
              <Table
                caption={t('admin.marketingSummary.messagingTitle')}
                head={
                  <tr>
                    <th scope="col" className={th}>
                      {t('admin.marketingSummary.channel')}
                    </th>
                    <th scope="col" className={thR}>
                      {t('admin.marketingSummary.sent')}
                    </th>
                    <th scope="col" className={thR}>
                      {t('admin.marketingSummary.delivered')}
                    </th>
                    <th scope="col" className={thR}>
                      {t('admin.marketingSummary.clicked')}
                    </th>
                  </tr>
                }
              >
                <tr className="border-t border-line">
                  <th scope="row" className={`${td} text-start font-normal`}>
                    {t('admin.marketingSummary.email')}
                  </th>
                  <td className={tdR}>{n(d.messaging.email.sent)}</td>
                  <td className={tdR}>{n(d.messaging.email.delivered)}</td>
                  <td className={tdR}>
                    {n(d.messaging.email.clicked)}
                    {d.messaging.email.clickBps !== null && (
                      <span className="ms-1 text-ink-3">({pct(d.messaging.email.clickBps)})</span>
                    )}
                  </td>
                </tr>
                {d.messaging.messages.map((m) => (
                  <tr key={m.channel} className="border-t border-line">
                    <th scope="row" className={`${td} text-start font-normal`}>
                      {t(`admin.marketingSummary.channels.${m.channel}`)}
                    </th>
                    <td className={tdR}>{n(m.sent)}</td>
                    <td className={tdR}>{n(m.delivered)}</td>
                    <td className={tdR}>—</td>
                  </tr>
                ))}
              </Table>
            </Section>
          )}

          {d.sources && (
            <Section
              title={t('admin.marketingSummary.sourcesTitle')}
              help={<HelpTip k="marketing_summary.sources" />}
            >
              {!d.sources.total ? (
                <EmptyState title={t('admin.marketingSummary.sourcesEmpty')} />
              ) : (
                <Table
                  caption={t('admin.marketingSummary.sourcesTitle')}
                  head={
                    <tr>
                      <th scope="col" className={th}>
                        {t('admin.marketingSummary.source')}
                      </th>
                      <th scope="col" className={thR}>
                        {t('admin.marketingSummary.orders')}
                      </th>
                      <th scope="col" className={thR}>
                        {t('admin.marketingSummary.share')}
                      </th>
                    </tr>
                  }
                >
                  {d.sources.rows.map((r) => (
                    <tr key={r.key} className="border-t border-line">
                      <th scope="row" className={`${td} text-start font-normal`}>
                        {r.kind === 'touch'
                          ? `${r.source} / ${r.medium ?? '—'}`
                          : t(`admin.marketingSummary.sourceKinds.${r.kind}`)}
                      </th>
                      <td className={tdR}>{n(r.orders)}</td>
                      <td className={tdR}>{pct(r.shareBps)}</td>
                    </tr>
                  ))}
                </Table>
              )}
              <p className="mt-2 text-sm">
                <Link to="/kaynaklar" className="text-pine hover:underline">
                  {t('admin.marketingSummary.openAttribution')}
                </Link>
              </p>
            </Section>
          )}
          {d.fxMissing > 0 && (
            <p className="text-xs text-amber">
              {t('admin.marketingSummary.fxMissing', { count: n(d.fxMissing) })}
            </p>
          )}
        </div>
      )}
    </PageFrame>
  );
}
